/**
 * Wraps Shopify's Customer Privacy API (`Shopify.customerPrivacy`) so components
 * can gate third-party marketing/tracking scripts on visitor consent.
 *
 * Ref: https://shopify.dev/docs/api/customer-privacy
 */

/**
 * Whether the current visitor has consented to marketing/tracking.
 *
 * Defaults to `false` when the API isn't available yet (e.g. it hasn't
 * finished loading on first paint) — third-party trackers must fail closed,
 * not open, since the risk being fixed is trackers firing before consent.
 */
export function isMarketingAllowed(): boolean {
  try {
    return (window as any).Shopify?.customerPrivacy?.marketingAllowed() ?? false;
  } catch {
    return false;
  }
}

/**
 * Runs `callback` once if marketing is already allowed, and again every time
 * the visitor's consent choice changes to allowed. Shopify dispatches
 * `visitorConsentCollected` on the `window` whenever consent is captured or
 * updated (banner interaction, GPC signal, preference-center change).
 *
 * Only fires on the *allowed* transition — callers use this to lazily inject
 * a script the first time it becomes allowed, not to un-inject it.
 */
export function onMarketingAllowed(callback: () => void): void {
  if (isMarketingAllowed()) {
    callback();
    return;
  }

  const handler = (): void => {
    if (isMarketingAllowed()) {
      window.removeEventListener("visitorConsentCollected", handler);
      callback();
    }
  };

  window.addEventListener("visitorConsentCollected", handler);
}
